import { useState } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useSite } from "../../context/SiteContext";
import { fileUrl } from "../../lib/api";
import { SectionHead } from "./Brand";

export const siteHref = (w) => (!w ? "#" : /^https?:\/\//i.test(w) ? w : `https://${w.toLowerCase()}`);

export const TextLogo = ({ name }) => (
  <div className="grid h-full w-full place-items-center bg-[radial-gradient(circle_at_50%_30%,#1c1a12,#0A0A0A)] px-3">
    <span className="text-center font-serif text-xl font-extrabold uppercase leading-tight tracking-wider gold-text">{name}</span>
  </div>
);

const Cred = ({ label, value, copied, testid }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); } catch { return; }
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-[#D4AF37]/20 bg-black/40 px-3 py-2">
      <span className="min-w-0 text-sm"><span className="text-zinc-500">{label}: </span><span className="font-semibold text-white" data-testid={testid}>{value}</span></span>
      <button onClick={copy} aria-label={`Copy ${label}`} data-testid={`${testid}-copy`} className="shrink-0 text-[#D4AF37] hover:text-[#F3D77A]">
        {done ? <span className="flex items-center gap-1 text-[10px] uppercase"><Check size={13} />{copied}</span> : <Copy size={14} />}
      </button>
    </div>
  );
};

export const Exchanges = ({ onJoin }) => {
  const { exch: t } = useLang().t;
  const { site } = useSite();
  const items = site?.exchanges || [];
  if (!items.length) return null;
  return (
    <section id="our-exchanges" className="mx-auto max-w-7xl px-4 pt-24 sm:px-6 lg:px-8" data-testid="exchanges-section">
      <SectionHead eyebrow={t.eyebrow} title={t.title} sub={t.sub} />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((e, i) => (
          <div key={e.id} className="exch-card group relative flex flex-col overflow-hidden rounded-2xl border border-[#D4AF37]/40 bg-[#0E0F14] transition-transform duration-300 hover:-translate-y-1" data-testid={`exchange-card-${i}`}>
            <div className="h-32 overflow-hidden border-b border-[#D4AF37]/25">
              {e.logo ? <img src={fileUrl(e.logo, site?.performance?.cdn_base_url)} alt={`${e.name} logo`} loading="lazy" className="h-full w-full object-contain bg-black p-3" /> : <TextLogo name={e.name} />}
            </div>
            <div className="flex flex-1 flex-col gap-3 p-5 text-center">
              <h3 className="font-serif text-lg font-bold uppercase text-[#D4AF37]" data-testid={`exchange-name-${i}`}>{e.name}</h3>
              <a href={siteHref(e.website)} target="_blank" rel="noreferrer nofollow" className="text-sm font-semibold tracking-wider text-white hover:text-[#F3D77A]" data-testid={`exchange-website-${i}`}>{e.website}</a>
              <p className="text-xs text-zinc-400">({e.bonus}) ({e.commission})</p>
              <div className="mt-1 space-y-2 text-left">
                <Cred label={t.user} value={e.demo_user} copied={t.copied} testid={`exchange-user-${i}`} />
                <Cred label={t.pass} value={e.demo_pass} copied={t.copied} testid={`exchange-pass-${i}`} />
              </div>
              <div className="mt-auto grid grid-cols-2 gap-2 pt-2">
                <a href={siteHref(e.website)} target="_blank" rel="noreferrer nofollow" data-testid={`exchange-visit-${i}`}
                  className="flex items-center justify-center gap-1.5 rounded-full border border-[#D4AF37]/50 py-2 text-[11px] font-semibold uppercase tracking-wider text-[#F3D77A] transition-colors hover:bg-[#D4AF37]/10">
                  {t.visit} <ExternalLink size={12} />
                </a>
                <button onClick={onJoin} data-testid={`exchange-getid-${i}`}
                  className="rounded-full bg-gradient-to-r from-[#F3D77A] via-[#D4AF37] to-[#9A7B2C] py-2 text-[11px] font-bold uppercase tracking-wider text-black">{t.getId}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
